import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  useLazyGetTrackingForecastDetailedViewVariablesQuery,
} from 'services/api/Meteorology/Fields/TrackingForecast/DetailedView';
import {
  selectCPMFTrackingForecastSubpage,
  selectCPMFTrackingForecastTimeFrequency,
  selectCPMFTrackingForecastModelId,
  selectCPMFTrackingForecastMembers,
  selectCPMFTrackingForecastMultiModelIds,
  selectCPMFTrackingForecastForecastDate,
  selectCPMFTrackingForecastLocationId,
} from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import { SUBPAGE_SUBMENU_OPTIONS } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';

export default function useVariableOptions() {
  const subpage = useSelector(selectCPMFTrackingForecastSubpage);
  const submenu = SUBPAGE_SUBMENU_OPTIONS[subpage];
  const timeFrequency = useSelector(selectCPMFTrackingForecastTimeFrequency);
  const modelId = useSelector(selectCPMFTrackingForecastModelId);
  const modelIds = useSelector(selectCPMFTrackingForecastMultiModelIds);
  const members = useSelector(selectCPMFTrackingForecastMembers);
  const forecastDate = useSelector(selectCPMFTrackingForecastForecastDate);
  const locationId = useSelector(selectCPMFTrackingForecastLocationId);

  const [trigger, {
    data, isFetching,
  }] = useLazyGetTrackingForecastDetailedViewVariablesQuery();

  const coreFields = useMemo(() => Boolean(timeFrequency), [timeFrequency]);
  const defaultFields = useMemo(() => Boolean(coreFields && modelId), [coreFields, modelId]);
  const multiModelFields = useMemo(() => (
    coreFields && locationId && modelIds.length > 0
  ), [coreFields, locationId, modelIds]);
  const multiMemberFields = useMemo(() => (
    defaultFields && members.length > 0
  ), [defaultFields, members]);

  const variables = useMemo(() => {
    if (!data) return [];

    switch (submenu) {
      case 'multi-modelos': return multiModelFields ? data : [];
      case 'multi-membros': return multiMemberFields ? data : [];
      default: return defaultFields ? data : [];
    }
  }, [submenu, data, defaultFields, multiMemberFields, multiModelFields]);

  useEffect(() => {
    switch (submenu) {
      case 'multi-modelos':
        if (multiModelFields) {
          trigger({
            submenu, timeFrequency, modelIds, forecastDate, locationId,
          });
        }
        break;
      case 'multi-membros':
        if (multiMemberFields) {
          trigger({
            submenu, timeFrequency, members, modelIds: [modelId],
          });
        }
        break;
      default:
        if (defaultFields) {
          trigger({ submenu, timeFrequency, modelIds: [modelId] });
        }
        break;
    }
  }, [
    submenu, timeFrequency, modelId, modelIds, members, forecastDate,
    defaultFields, multiMemberFields, multiModelFields, locationId,
    trigger,
  ]);

  return { variables, isFetching };
}
